import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Rss, UploadCloud } from "lucide-react";

const CORPUS_STATUSES = [
  "Discovered", "Rights Review", "Approved for Metadata", "Approved for Full Ingestion",
  "Processing", "Needs Editorial Review", "Published to Corpus", "Excluded", "Archived", "Superseded",
];

export default function CorpusPanel() {
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState("https://podcasts.apple.com/us/podcast/the-impactvest-podcast-transformative-global/id1634351311");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "All") params.corpus_status = filter;
      if (q) params.q = q;
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/corpus/assets`, { headers: adminHeaders(), params }),
        axios.get(`${API}/admin/corpus/stats`, { headers: adminHeaders() }),
      ]);
      setAssets(a.data || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to load corpus.");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const setStatus = async (id, corpus_status) => {
    try {
      await axios.patch(`${API}/admin/corpus/assets/${id}`, { corpus_status }, { headers: adminHeaders() });
      setAssets((list) => list.map((x) => (x.id === id ? { ...x, corpus_status } : x)));
      toast.success(`Status → ${corpus_status}`);
      load();
    } catch { toast.error("Unable to update."); }
  };

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/admin/corpus/seed`, {}, { headers: adminHeaders() });
      toast.success(`Seeded ${r.data.seeded}, skipped ${r.data.skipped}`);
      load();
    } catch { toast.error("Seed failed."); }
  };

  const importFeed = async () => {
    if (!feedUrl) return;
    toast("Importing feed metadata…");
    try {
      const r = await axios.post(`${API}/admin/corpus/podcast/import`, { feed_url: feedUrl }, { headers: adminHeaders() });
      toast.success(`Found ${r.data.episodes_found}, created ${r.data.created}, skipped ${r.data.skipped}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed. Feed may not expose RSS."); }
  };

  return (
    <div data-testid="admin-corpus">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 border border-iv-line mb-6">
          <MiniStat label="Total" value={stats.total} tone="gold" />
          <MiniStat label="Published" value={stats.published} />
          <MiniStat label="Rights Review" value={stats.by_status?.["Rights Review"] ?? 0} />
          <MiniStat label="Discovered" value={stats.by_status?.["Discovered"] ?? 0} />
          <MiniStat label="Podcasts" value={stats.by_type?.["Podcast"] ?? 0} />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 border border-iv-line bg-iv-surface">
        <div className="flex-1 min-w-[280px]">
          <div className="font-mono-tag text-iv-fg-3 mb-2 flex items-center gap-2"><Rss size={12} /> Podcast / RSS Feed Import (metadata only)</div>
          <input value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} className="w-full bg-transparent border-b border-iv-line-strong py-2 text-[13px] outline-none focus:border-iv-green" placeholder="https://…/feed.xml" />
        </div>
        <button onClick={importFeed} className="iv-btn"><UploadCloud size={15} /> Import Feed</button>
        <button onClick={seed} className="iv-btn">Seed Initial Sources</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search corpus…" className="bg-transparent border-b border-iv-line-strong py-1.5 text-[13px] outline-none focus:border-iv-green min-w-[200px]" />
        {["All", ...CORPUS_STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`iv-chip ${filter === s ? "iv-chip--gold" : ""}`}>{s}</button>
        ))}
        <button onClick={load} className="iv-btn iv-btn--ghost ml-auto"><RefreshCw size={14} /> {loading ? "…" : "Refresh"}</button>
      </div>

      <div className="border-t border-iv-line">
        {assets.length === 0 ? (
          <p className="py-12 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">No assets in this view.</p>
        ) : assets.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-3 items-center px-3 py-4 border-b border-iv-line hover:bg-white/40">
            <div className="col-span-12 md:col-span-6">
              <div className="text-[14px] text-iv-fg leading-snug">{a.title}</div>
              <div className="font-mono text-[10px] text-iv-fg-3 mt-1 truncate">{a.publisher || a.author} · {a.rights_status} · {a.citation_layer}</div>
            </div>
            <div className="col-span-6 md:col-span-2"><span className="iv-chip">{a.asset_type}</span></div>
            <div className="col-span-6 md:col-span-4 md:justify-self-end">
              <select value={a.corpus_status} onChange={(e) => setStatus(a.id, e.target.value)}
                className="bg-iv-surface border border-iv-line-strong text-[11px] px-2 py-1 font-mono uppercase tracking-wider text-iv-fg">
                {CORPUS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div className={`px-4 py-3 border-r last:border-r-0 border-iv-line ${tone === "gold" ? "bg-white/40" : ""}`}>
      <div className="font-mono-tag text-iv-fg-3">{label}</div>
      <div className={`font-serif-display text-[26px] leading-none mt-1 ${tone === "gold" ? "text-iv-gold" : "text-iv-fg"}`}>{value}</div>
    </div>
  );
}
